import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useAutoContentEnhancement } from '@/hooks/useAutoContentEnhancement';
import { Activity, XCircle, StopCircle, RotateCcw, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ActiveOperation {
  id: string;
  status: string;
  total_items: number;
  processed_items: number;
  successful_items: number;
  failed_items: number;
  started_at: string;
}

export const ActiveEnhancementMonitor = () => {
  const [activeOperation, setActiveOperation] = useState<ActiveOperation | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const {
    isRunning,
    progress,
    error,
    forceStopEnhancement,
    resetEnhancementState
  } = useAutoContentEnhancement();

  const handleResetState = async () => {
    setIsResetting(true);
    try {
      await resetEnhancementState();
      window.location.reload();
    } catch (error) {
      console.error('Failed to reset state:', error);
    } finally {
      setIsResetting(false);
    }
  };

  // Subscribe to real-time updates for active operation
  useEffect(() => {
    const loadActiveOperation = async () => {
      const { data } = await supabase
        .from('content_enhancement_operations')
        .select('*')
        .eq('status', 'in_progress')
        .order('started_at', { ascending: false })
        .limit(1)
        .single();

      if (data) {
        setActiveOperation(data);
      } else {
        setActiveOperation(null);
      }
    };

    loadActiveOperation();

    const channel = supabase
      .channel('enhancement_operations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'content_enhancement_operations'
        },
        () => {
          loadActiveOperation();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {activeOperation || isRunning ? (
            <>
              <Activity className="h-5 w-5 animate-pulse text-blue-600" />
              Enhancement System Active
            </>
          ) : (
            'Enhancement System Status'
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">System Actions</span>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={forceStopEnhancement}
              disabled={!activeOperation && !isRunning}
            >
              <StopCircle className="mr-2 h-4 w-4" />
              Force Stop
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetState}
              disabled={isResetting}
            >
              {isResetting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Reset State
            </Button>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex gap-2">
          {(activeOperation || isRunning) && (
            <Badge variant="default" className="animate-pulse">
              Processing...
            </Badge>
          )}
          {error && (
            <Badge variant="destructive">
              <XCircle className="mr-1 h-3 w-3" />
              Error
            </Badge>
          )}
          {!activeOperation && !isRunning && !error && (
            <Badge variant="outline">
              Ready
            </Badge>
          )}
        </div>

        {/* Show active operation from database */}
        {activeOperation && (
          <div className="space-y-3 p-4 rounded-lg border bg-blue-50/50 dark:bg-blue-950/20">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Bulk Content Enhancement
              </span>
              <Badge variant="default" className="animate-pulse">
                Live
              </Badge>
            </div>

            <Progress 
              value={(activeOperation.processed_items / activeOperation.total_items) * 100} 
              className="h-2" 
            />

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Progress</p>
                <p className="font-bold text-lg">
                  {activeOperation.processed_items} / {activeOperation.total_items}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Success Rate</p>
                <p className="font-bold text-lg text-green-600">
                  {activeOperation.processed_items > 0 
                    ? Math.round((activeOperation.successful_items / activeOperation.processed_items) * 100)
                    : 0}%
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Successful</p>
                <p className="font-semibold text-green-600">
                  {activeOperation.successful_items}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Failed</p>
                <p className="font-semibold text-red-600">
                  {activeOperation.failed_items}
                </p>
              </div>
            </div>

            <Alert className="border-blue-200 bg-blue-50/30">
              <Activity className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-xs text-blue-900">
                🚀 Server processing chunks automatically. Safe to close browser.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Progress details if any */}
        {progress && !activeOperation && (
          <div className="rounded-lg border p-4 text-sm space-y-2">
            <p className="text-muted-foreground">Last operation stats:</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground">Processed:</span>
                <span className="ml-2 font-medium">{progress.processedModules}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total:</span>
                <span className="ml-2 font-medium">{progress.totalModules}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Success:</span>
                <span className="ml-2 font-medium text-success">{progress.successCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Failed:</span>
                <span className="ml-2 font-medium text-destructive">{progress.errorCount}</span>
              </div>
            </div>
          </div>
        )}

        <Alert>
          <AlertDescription className="text-sm">
            ℹ️ All enhanced content is preserved in the database. Use the stats above to see real-time content quality metrics.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};
